/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
export { fetch, secrets, invoke, output, describe, query, read, write, blob, Capabilities, };
class Capabilities {
    #capabilities = new Map();
    static #instance = new Capabilities();
    constructor() { }
    async invoke(invocationId, name, inputs) {
        const installed = this.#capabilities.get(invocationId);
        const capability = installed?.capabilities.get(name);
        if (!installed || !capability) {
            throw new Error(`Capability "${name}" is not avaialble for invocation "${invocationId}".`);
        }
        const parsedInputs = JSON.parse(inputs);
        const isOutput = name === "output";
        const metadata = parsedInputs.$metadata;
        if (metadata && !isOutput) {
            delete parsedInputs.$metadata;
        }
        const path = (await installed.telemetry?.startCapability(name, parsedInputs, metadata)) || 0;
        const outputs = await capability(parsedInputs, installed.telemetry?.invocationPath(path) || []);
        await installed.telemetry?.endCapability(name, path, parsedInputs, outputs);
        return JSON.stringify(outputs);
    }
    install(invocationId, capabilities, telemetry) {
        if (this.#capabilities.has(invocationId)) {
            throw new Error(`Invocation ID collision: "${invocationId}" capabilities were already installed.`);
        }
        this.#capabilities.set(invocationId, {
            telemetry,
            capabilities: new Map(Object.entries(capabilities)),
        });
    }
    uninstall(invocationId) {
        this.#capabilities.delete(invocationId);
    }
    static instance() {
        return this.#instance;
    }
}
async function fetch(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "fetch", inputs);
}
async function secrets(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "secrets", inputs);
}
async function invoke(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "invoke", inputs);
}
async function output(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "output", inputs);
}
async function describe(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "describe", inputs);
}
async function query(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "query", inputs);
}
async function read(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "read", inputs);
}
async function write(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "write", inputs);
}
async function blob(invocationId, inputs) {
    return Capabilities.instance().invoke(invocationId, "blob", inputs);
}
//# sourceMappingURL=capabilities.js.map